/* eslint-disable camelcase */
import React from 'react';

import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { cioFacetsToUrlFilters } from '~/grid/utils/cioUtils';
import { slugify, useBreakpoint } from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';
import { useDomain } from '~/techstyle-shared/redux-core';

import useProductGridData from '../../hooks/useProductGridData';
import {
  isFacetOptionSelected,
  updateFiltersInUrlParams,
  sanitizeFilterName,
  getTranslatedFacetOptionDisplayName,
} from '../../utils/filters';
import { scrollToTopOfGrid } from '../../utils/scroll';
import CheckboxInput from '../SharedGridFiltersUi/CheckboxInput';

const DynamicFormattedMessage = FormattedMessage;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: 0.25rem;
  gap: 8px;
`;

const SubFacetHeading = styled.h3`
  display: flex;
  padding-top: 0.5rem;
  ${({ theme }) => theme.gridFilterStyles.nestedHeadingStyle};
`;

const Fieldset = styled.fieldset`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding-left: 0.75rem;
  border: none;
`;

const Legend = styled.legend`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
`;

const DesktopGridGroupedFilter = ({ sub_facets = [], name }) => {
  const { selectedUrlFilters } = useProductGridData();
  const router = useRouter();
  const { isMobile } = useBreakpoint();
  const domain = useDomain();

  if (!sub_facets?.length) {
    return null;
  }

  const handleOnChange = ({ facetName, facetOption }) => {
    updateFiltersInUrlParams({
      facetName,
      facetOption,
      router,
    });
    scrollToTopOfGrid({ isMobile });
  };

  return (
    <Wrapper>
      {sub_facets.map((subFacet) => {
        return (
          <React.Fragment key={subFacet.name}>
            <SubFacetHeading>
              <DynamicFormattedMessage
                id={`site_product_filter.${slugify(
                  cioFacetsToUrlFilters[subFacet.name] || subFacet.name
                )}`}
                defaultMessage={subFacet.display_name}
              />
            </SubFacetHeading>
            <Fieldset>
              <Legend>{subFacet.display_name}</Legend>
              {subFacet.options?.map((option) => {
                const inputId = `${subFacet.name}-${option.value}`;
                const isSelected = isFacetOptionSelected({
                  facetName: subFacet.name,
                  facetOption: option.value,
                  selectedUrlFilters,
                });
                const facetOptionDisplayName =
                  getTranslatedFacetOptionDisplayName({
                    facetName: name,
                    facetOption: option,
                    domain,
                  });

                return (
                  <CheckboxInput
                    key={option.value}
                    id={inputId}
                    name={option.value}
                    label={facetOptionDisplayName}
                    checked={isSelected}
                    onChange={() =>
                      handleOnChange({
                        facetName: subFacet.name,
                        facetOption: option.value,
                      })
                    }
                    filterName={sanitizeFilterName(subFacet.name)}
                  />
                );
              })}
            </Fieldset>
          </React.Fragment>
        );
      })}
    </Wrapper>
  );
};

DesktopGridGroupedFilter.propTypes = {
  name: PropTypes.string.isRequired,
  sub_facets: PropTypes.arrayOf(
    PropTypes.shape({
      display_name: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          display_name: PropTypes.string.isRequired,
          value: PropTypes.string.isRequired,
        })
      ).isRequired,
    })
  ),
};

export default DesktopGridGroupedFilter;
